import { GET, POST, PUT, DELETE } from './executer';
import apiServiceInstance from './apiService';

/**
 * Alert type categories
 */
export type AlertType =
  | 'air_temperature'
  | 'rain_risk'
  | 'weather_alert'
  | 'track_condition'
  | 'system_status'
  | 'general';

/**
 * Alert severity levels
 */
export type AlertSeverity = 'info' | 'warning' | 'critical';

/**
 * Time unit for notify_before entries
 */
export type NotifyBeforeUnit = 'minutes' | 'hours' | 'days';

/**
 * Advance notification time entry
 */
export interface NotifyBeforeEntry {
  value: number;
  unit: NotifyBeforeUnit;
}

/**
 * Event Alert entity
 */
export interface EventAlert {
  id: string;
  season_year: number;
  circuit_id: string;
  start_date: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  sessions: string[];           // Associated sessions - empty for event-wide alerts
  roles: string[];              // Roles that can view/receive this alert - empty means visible to all
  is_automated: boolean;
  created_by: string;
  start_time: string;           // When alert becomes relevant (UTC)
  end_time: string;             // When alert expires (UTC)
  show_immediately: boolean;    // Push notification immediately upon creation
  notify_before: NotifyBeforeEntry[]; // Advance notification schedule
  notify_on_start: boolean;     // Show notification when start_time is reached
  notify_on_end: boolean;       // Show notification when end_time is reached
  created_at: string;
  updated_at: string | null;
  updated_by: string | null;
  requires_acknowledgment: boolean;
  is_deleted: boolean;
}

/**
 * Request body for creating an alert
 */
export interface CreateEventAlertRequest {
  alert_type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  sessions?: string[];          // Associated sessions - empty for event-wide alerts
  roles?: string[];             // Roles that can view/receive this alert - omit or empty for all roles
  start_time?: string;          // Defaults to now if not provided
  end_time: string;             // Required - when the alert expires
  show_immediately?: boolean;   // Push notification immediately upon creation
  notify_before?: NotifyBeforeEntry[]; // Advance notification schedule
  notify_on_start?: boolean;    // Show notification when start_time is reached
  notify_on_end?: boolean;      // Show notification when end_time is reached
  requires_acknowledgment?: boolean;
}

/**
 * Request body for updating an alert
 */
export interface UpdateEventAlertRequest {
  alert_type?: AlertType;
  severity?: AlertSeverity;
  title?: string;
  message?: string;
  sessions?: string[];
  roles?: string[];             // Roles that can view/receive this alert - empty for all roles
  start_time?: string;
  end_time?: string | null;
  show_immediately?: boolean;
  notify_before?: NotifyBeforeEntry[];
  notify_on_start?: boolean;
  notify_on_end?: boolean;
  requires_acknowledgment?: boolean;
  is_deleted?: boolean;
}

/**
 * Alert type display configuration
 */
export const ALERT_TYPE_CONFIG: Record<AlertType, { label: string; color: string; icon: string }> = {
  air_temperature: { label: 'Air Temperature', color: '#fa8c16', icon: '🌡️' },
  rain_risk: { label: 'Rain Risk', color: '#1890ff', icon: '🌧️' },
  weather_alert: { label: 'Weather Alert', color: '#f5222d', icon: '⚠️' },
  track_condition: { label: 'Track Condition', color: '#52c41a', icon: '🏁' },
  system_status: { label: 'System Status', color: '#722ed1', icon: '⚙️' },
  general: { label: 'General', color: '#595959', icon: '📢' },
};

/**
 * Severity display configuration
 */
export const SEVERITY_CONFIG: Record<AlertSeverity, { label: string; color: string }> = {
  info: { label: 'Info', color: '#1890ff' },
  warning: { label: 'Warning', color: '#faad14' },
  critical: { label: 'Critical', color: '#f5222d' },
};

/**
 * Helper function to convert NotifyBeforeEntry to milliseconds
 */
export function notifyBeforeToMs(entry: NotifyBeforeEntry): number {
  switch (entry.unit) {
    case 'minutes':
      return entry.value * 60 * 1000;
    case 'hours':
      return entry.value * 60 * 60 * 1000;
    case 'days':
      return entry.value * 24 * 60 * 60 * 1000;
    default:
      return 0;
  }
}

/**
 * Helper function to calculate all notification times for an alert
 */
export function calculateNotificationTimes(alert: EventAlert): Date[] {
  const times: Date[] = [];
  const startTime = new Date(alert.start_time);
  
  // Add immediate notification time (created_at) if show_immediately is true
  if (alert.show_immediately) {
    times.push(new Date(alert.created_at));
  }
  
  // Add scheduled notification times based on notify_before entries
  for (const entry of alert.notify_before) {
    const ms = notifyBeforeToMs(entry);
    const notifyTime = new Date(startTime.getTime() - ms);
    times.push(notifyTime);
  }
  
  return times.sort((a, b) => a.getTime() - b.getTime());
}

/**
 * Helper function to check if a user role can view/receive an alert
 * Returns true if:
 * - Alert has no roles defined (empty array) - visible to everyone
 * - User's role is included in the alert's roles array
 */
export function canRoleViewAlert(alert: EventAlert, userRole: string): boolean {
  // Empty roles array means visible to all
  if (!alert.roles || alert.roles.length === 0) {
    return true;
  }
  return alert.roles.includes(userRole);
}

/**
 * Service for managing event alerts
 */
class AlertsService {
  /**
   * Get the source query string based on isDemo flag
   * Follows the same pattern as other routes (source=demo)
   */
  private getSourceParam(isDemo: boolean): string {
    return isDemo ? 'source=demo' : 'source=prod';
  }

  /**
   * Fetch alerts for a specific event
   * @param isDemo - Whether this is a demo event (uses demo_event_alerts table)
   */
  async getEventAlerts(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    isDemo: boolean = false,
    includeDeleted: boolean = false
  ) {
    const params = new URLSearchParams();
    params.append('source', isDemo ? 'demo' : 'prod');
    if (includeDeleted) params.append('include_deleted', 'true');
    const url = `/api/alerts/${seasonYear}/${circuitId}/${startDate}?${params.toString()}`;
    
    return await apiServiceInstance.client.execute<EventAlert[]>(
      GET,
      url
    );
  }

  /**
   * Fetch a single alert by ID
   * @param isDemo - Whether to look in demo_event_alerts table
   */
  async getAlertById(id: string, isDemo: boolean = false) {
    const url = `/api/alerts/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventAlert>(
      GET,
      url
    );
  }

  /**
   * Create a new alert
   * @param isDemo - Whether to insert into demo_event_alerts table
   */
  async createAlert(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    alert: CreateEventAlertRequest,
    isDemo: boolean = false
  ) {
    const url = `/api/alerts/${seasonYear}/${circuitId}/${startDate}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventAlert>(
      POST,
      url,
      alert
    );
  }

  /**
   * Update an existing alert
   * @param isDemo - Whether to update in demo_event_alerts table
   */
  async updateAlert(id: string, updates: UpdateEventAlertRequest, isDemo: boolean = false) {
    const url = `/api/alerts/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<EventAlert>(
      PUT,
      url,
      updates
    );
  }

  /**
   * Delete an alert (soft delete)
   * @param isDemo - Whether to delete from demo_event_alerts table
   */
  async deleteAlert(id: string, isDemo: boolean = false) {
    const url = `/api/alerts/id/${id}?${this.getSourceParam(isDemo)}`;
    
    return await apiServiceInstance.client.execute<{ message: string }>(
      DELETE,
      url
    );
  }

  /**
   * Format timestamp for display in the circuit's local timezone
   */
  formatTimestamp(timestamp: string, timeZone?: string): string {
    const date = new Date(timestamp);
    
    if (!timeZone) {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  /**
   * Get alert type label
   */
  getAlertTypeLabel(alertType: AlertType): string {
    return ALERT_TYPE_CONFIG[alertType]?.label || alertType;
  }

  /**
   * Get severity label
   */
  getSeverityLabel(severity: AlertSeverity): string {
    return SEVERITY_CONFIG[severity]?.label || severity;
  }
}

const alertsServiceInstance = new AlertsService();
export default alertsServiceInstance;
